import { useEffect, useMemo, useState } from "react";
import { CampaignType } from "@adlywise/shared";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import Card from "@/components/common/Card";
import MultiSelect from "@/components/common/MultiSelect";

const parseBlockList = (raw) =>
  raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export default function Step2Target({
  form,
  segments,
  providers,
  onFieldChange,
  onNumberChange,
}) {
  const isTransit = form.type === CampaignType.Transit;

  const segmentOptions = useMemo(
    () =>
      segments
        .map((segment) => ({
          value: segment.uuid,
          label: segment.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [segments],
  );

  const transitPublisherOptions = useMemo(
    () =>
      (Array.isArray(providers) ? providers : [])
        .filter(
          (p) =>
            Array.isArray(p.types) &&
            p.types.includes("rtb") &&
            p.allowTransit === true,
        )
        .map((p) => ({ value: p.uuid, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  const standardPublisherOptions = useMemo(
    () =>
      (Array.isArray(providers) ? providers : [])
        .map((p) => ({ value: p.uuid, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  const handleTransitField = (field, value) => {
    onFieldChange("metadata", { ...form.metadata, [field]: value });
  };

  // The block list is stored as an array, but edited as free text. Keep a local
  // text buffer so Enter/commas/empty lines survive keystrokes instead of being
  // stripped by parsing on every change (which would fight the caret). Resync
  // only when the external array diverges from what our text parses to (e.g.
  // loading an existing campaign), never mid-typing.
  const [blockListText, setBlockListText] = useState(() =>
    (form.metadata.blockList ?? []).join("\n"),
  );

  useEffect(() => {
    const external = (form.metadata.blockList ?? []).join("\n");
    if (parseBlockList(blockListText).join("\n") !== external) {
      setBlockListText(external);
    }
  }, [form.metadata.blockList]);

  if (isTransit) {
    return (
      <Card className="builder-form flex flex-col gap-5">
        <div>
          <h3 className="text-base font-semibold text-(--text-primary)">
            Transit configuration
          </h3>
          <p className="mt-1 text-sm text-(--text-secondary)">
            Configure the transit entrypoint and RTB publishers that allow
            transit.
          </p>
        </div>

        <div className="form-group-modal">
          <label htmlFor="transit-entrypoint">Entrypoint URL</label>
          <input
            id="transit-entrypoint"
            type="url"
            value={form.metadata.entrypoint}
            onChange={(e) => handleTransitField("entrypoint", e.target.value)}
            placeholder="https://example.com/bid"
          />
          <p className="form-help-text">
            The HTTPS URL that receives proxied bid requests.
          </p>
        </div>

        <div className="builder-grid two-columns">
          <div className="form-group-modal">
            <label htmlFor="transit-margin">Margin (%)</label>
            <input
              id="transit-margin"
              type="number"
              min="0"
              step="1"
              value={form.metadata.margin}
              onChange={(e) => handleTransitField("margin", e.target.value)}
              placeholder="e.g. 10"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="transit-qps">QPS</label>
            <input
              id="transit-qps"
              type="number"
              min="1"
              step="1"
              value={form.metadata.qps}
              onChange={(e) => handleTransitField("qps", e.target.value)}
              placeholder="e.g. 100"
            />
          </div>
        </div>

        <div className="form-group-modal">
          <label>Publishers (RTB + allowTransit)</label>
          <div className="mt-1">
            {transitPublisherOptions.length > 0 ? (
              <MultiSelect
                options={transitPublisherOptions}
                value={form.metadata.selectedPublishers}
                onChange={(next) =>
                  handleTransitField("selectedPublishers", next)
                }
                placeholder="Search publishers..."
              />
            ) : (
              <p className="form-help-text">
                No eligible publishers found. Publishers must have RTB type and
                allowTransit enabled.
              </p>
            )}
          </div>
        </div>

        <div className="form-group-modal">
          <label htmlFor="transit-blocklist">Block list</label>
          <textarea
            id="transit-blocklist"
            value={blockListText}
            onChange={(e) => {
              setBlockListText(e.target.value);
              handleTransitField("blockList", parseBlockList(e.target.value));
            }}
            placeholder="One domain per line, or comma-separated"
            rows={4}
          />
          <p className="form-help-text">
            Domains or identifiers to block from transit routing.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="builder-form flex flex-col gap-5">
      <div>
        <h3 className="text-base font-semibold text-(--text-primary)">
          Target audiences
        </h3>
        <p className="mt-1 text-sm text-(--text-secondary)">
          Choose which audiences this campaign should reach. Leave empty to
          target everyone.
        </p>
        <div className="mt-3">
          {segmentOptions.length > 0 ? (
            <MultiSelect
              options={segmentOptions}
              value={form.selectedSegments}
              onChange={(next) => onFieldChange("selectedSegments", next)}
              placeholder="Search audiences..."
            />
          ) : (
            <p className="form-help-text">
              No audiences available yet.{" "}
              <Link
                to="/dashboard/segments/new"
                className="font-medium text-(--brand-primary) hover:underline"
              >
                <Plus size={12} strokeWidth={2.5} className="inline" /> Create
                an audience
              </Link>{" "}
              first to target it.
            </p>
          )}
        </div>
      </div>

      <div className="form-group-modal">
        <label>Publishers (optional)</label>
        <div className="mt-1">
          <MultiSelect
            options={standardPublisherOptions}
            value={form.metadata.selectedPublishers}
            onChange={(next) => handleTransitField("selectedPublishers", next)}
            placeholder="Search publishers…"
          />
        </div>
        <p className="form-help-text">
          Restrict this campaign to selected traffic providers. Leave empty to
          run on all providers.
        </p>
      </div>

      <div className="builder-divider" aria-hidden />

      <div className="builder-subsection">
        <h4>Budget &amp; delivery</h4>
        <p className="subsection-description">
          Optional caps and CPM override for this campaign.
        </p>
        <div className="builder-grid two-columns">
          <div className="form-group-modal">
            <label htmlFor="campaign-cpm">CPM ($)</label>
            <input
              id="campaign-cpm"
              type="number"
              min="0"
              step="0.01"
              value={form.cpm ?? 0}
              onChange={(event) => onNumberChange("cpm", event.target.value)}
              placeholder="e.g. 2.50"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-budget-cap">Budget cap ($)</label>
            <input
              id="campaign-budget-cap"
              type="number"
              min="0"
              step="0.01"
              value={form.budgetCap ?? ""}
              onChange={(event) =>
                onFieldChange("budgetCap", event.target.value)
              }
              placeholder="e.g. 5000"
            />
          </div>
        </div>
        <div className="builder-grid three-columns">
          <div className="form-group-modal">
            <label htmlFor="campaign-daily-freq">Daily frequency cap</label>
            <input
              id="campaign-daily-freq"
              type="number"
              min="1"
              value={form.dailyFrequency ?? ""}
              onChange={(event) =>
                onFieldChange("dailyFrequency", event.target.value)
              }
              placeholder="e.g. 3"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-weekly-freq">Weekly frequency cap</label>
            <input
              id="campaign-weekly-freq"
              type="number"
              min="1"
              value={form.weeklyFrequency ?? ""}
              onChange={(event) =>
                onFieldChange("weeklyFrequency", event.target.value)
              }
              placeholder="e.g. 10"
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="campaign-impressions-cap">Impressions cap</label>
            <input
              id="campaign-impressions-cap"
              type="number"
              min="1"
              value={form.impressionsCap ?? ""}
              onChange={(event) =>
                onFieldChange("impressionsCap", event.target.value)
              }
              placeholder="e.g. 100000"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
